import { CSSObject } from "@emotion/core";
import styled from "@emotion/styled";
import { sizes } from "../../../../constants/spacing";
import { createFocusRingStyles } from "../../../../util/createFocusRingStyles";
import { RequiredThemeKeys } from "../../../../util/theme";
import { Clickable } from "../../../clickable";
import { formInputTheme } from "../../../textinput/styled/formInputTheme";
import { selectV2Theme } from "../selectV2Theme";

interface ActionButtonBaseProps {
    themeMode: RequiredThemeKeys;
}

export const ActionButtonBaseCss = ({ themeMode }: ActionButtonBaseProps): CSSObject => ({
    display: "inline-flex",
    alignItems: "center",
    boxSizing: "border-box",
    cursor: "pointer",
    height: selectV2Theme.actionButtons.size(themeMode),
    width: selectV2Theme.actionButtons.size(themeMode),
    margin: 0,
    padding: `${sizes.xxSmall}px`,
    border: "none",
    borderRadius: selectV2Theme.actionButtons.borderRadius(themeMode),
    fontWeight: 600,
    color: "currentColor",
    justifyContent: "space-around",
    whiteSpace: "nowrap",
    backgroundColor: "transparent",
});

interface ActionButtonProps {
    themeMode: RequiredThemeKeys;
    isActive?: boolean;
    isHovered?: boolean;
    isOpen?: boolean;
    omitFocusRing?: boolean;
}

export const ActionButton = styled(Clickable)<ActionButtonProps>`
    ${ActionButtonBaseCss}

    ${({ omitFocusRing, themeMode }) =>
        omitFocusRing
            ? ""
            : createFocusRingStyles({
                  theme: themeMode,
                  // We want these outlines to sit within the button's content box.
                  gap: -2,
                  focusSelector: ":focus-visible",
                  componentBorderWidth: 0,
                  componentRadius: formInputTheme.borderRadius(themeMode),
              })}
    background-color: ${({ isHovered, isOpen, isActive, themeMode }) => {
        if (isHovered && !isActive) {
            return `${selectV2Theme.actionButtons.background.hover(themeMode)};`;
        }
        if (isOpen || isActive) {
            return `${selectV2Theme.actionButtons.background.active(themeMode)};`;
        }
        return ``;
    }}
`;
ActionButton.displayName = "ActionButton";
